\
<template>
    <Card title="On Hold Queue">
        <b-form-row class="mb-2">
            <b-col>
                <SearchComponent label="Store" v-model="store" searchURL="/Store" prepopulate clearable />
            </b-col>
            <b-col>
                <label>
                    SubStatus:
                </label>
                <v-select
                    :value="{ value: subStatus, label: onHoldSubStatuses.find(ss => ss.value == subStatus)?.label }"
                    :options="activeOnHoldSubStatuses" @input="subStatus = $event?.value" />
            </b-col>
            <b-col>
                <DateRangeFilter v-model="dateFilter" label="Date Range:" :clearable="false" />
            </b-col>
        </b-form-row>
        <b-row>
            <b-col>
                <b-table id="on-hold-table" ref="onHoldTable" api-url="Prescription/OnHold" :items="onHoldTableProvider"
                    :fields="fields" :filter="tableFilter" responsive="xl" sort-icon-left show-empty
                    :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" no-local-sorting @row-clicked="selectRx">
                    <template #cell(actions)="data">
                        <div class="table-icon-actions">
                            <router-link :to="getPrescriptionDetailsRoute(data.item)" class="mr-1">
                                <b-icon icon="pencil-square" v-b-tooltip.hover title="Edit" />
                            </router-link>
                        </div>
                    </template>
                    <template #cell(rxId)="data">
                        <span v-b-tooltip.hover :title="`Memo: ${data.item.memo ?? ''}`">
                            {{ data.item.rxId }}
                        </span>
                    </template>
                </b-table>

                <BottomPagerBar v-model="pageNumber" :per-page="pageSize" :loading="loading"
                    :total-rows="totalItemCount" show-page-size-selector show-total item-name="prescription"
                    @change-page-size="pageSize = $event" />
            </b-col>
        </b-row>
    </Card>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import SearchComponent from "@/components/SearchComponent.vue";
import { Store } from "@/models/Store";
import Axios, { AxiosRequestConfig } from "axios";
import { Prescription } from "@/models/Prescription";
import EnumSelector from '@/components/EnumSelector.vue';
import { BvTableCtxObject } from "bootstrap-vue";
import { Patient } from "@/models/Patient";
import moment from "moment";
import { DatePicker } from "element-ui";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import DateRangeFilter from "@/components/DateRangeFilter.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    components: {
        SearchComponent,
        EnumSelector,
        [DatePicker.name]: DatePicker,
        BottomPagerBar,
        DateRangeFilter
    }
})
export default class PrescriptionOnHoldQueue extends Vue {
    private store: Store = new Store();
    private subStatus: number | null = null;

    private dateFilter: { startDate: any, endDate: any } = {
        startDate: moment().subtract(14, "days").toDate(),
        endDate: new Date()
    };

    private pageNumber = 1;
    private pageSize = 100;
    private loading = true;
    private totalItemCount = 200;

    private sortBy: string = "fillDate";
    private sortDesc: boolean = false;

    mounted() {
        this.getOnHoldSubStatuses();
    }

    readonly fields = [
        { key: "productNameShort", label: "Drug", sortable: true },
        { key: "patient", label: "Patient", formatter: this.patientFormatter, sortable: true },
        { key: "storeName", label: "Store", sortable: true },
        { key: "rxId", label: "Rx Id", sortable: true },
        { key: "quantity", label: "Quantity" },
        { key: "subStatus", label: "Hold Status", formatter: this.subStatusLabel, sortable: true },
        { key: "fillDate", label: "Fill Date", formatter: (d: Date) => moment.utc(d).format("L"), sortable: true },
        { key: "programName", label: "Program", sortable: true },
        { key: "actions", label: "" }
    ];

    get tableFilter() {
        const filter: any = {
            pageNumber: this.pageNumber,
            pageSize: this.pageSize,
            fromDate: this.dateFilter.startDate,
            toDate: this.dateFilter.endDate,
            sortBy: this.sortBy,
            sortDirection: this.sortDesc ? "D" : "A"
        };

        // Only include optional parameters if they have valid values
        if (this.store?.id) {
            filter.storeId = this.store.id;
        }
        if (this.subStatus !== null && this.subStatus !== undefined) {
            filter.subStatus = this.subStatus;
        }

        return filter;
    }

    @Watch("sortBy")
    @Watch("sortDesc")
    resetPageNumber() {
        this.pageNumber = 1;
    }

    patientFormatter(pt: Patient) {
        const pat = Object.assign(new Patient(), pt);
        return pat.displayNamesForPerson();
    }

    async onHoldTableProvider(ctx: BvTableCtxObject) {
        const filter = ctx.filter as any;
        const config: AxiosRequestConfig = {
            params: filter,
        };
        this.loading = true;
        try {
            const resp = await Axios.get<any>(ctx.apiUrl!, config);
            this.totalItemCount = resp.data.count;
            return resp.data.items;
        } catch (err) {
            console.warn(err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }

        return [];
    }

    selectRx(item: Prescription, index?: number, event?: any) {
        const route: any = this.getPrescriptionDetailsRoute(item);
        this.$router.push(route);
    }

    getPrescriptionDetailsRoute(rxRow: Prescription) {
        return {
            name: 'PrescriptionDetails',
            params: {
                storeID: rxRow.storeID,
                rxNumber: rxRow.rxNumber,
                rfNumber: (rxRow.rfNumber || 0)
            }
        };
    }

    private onHoldSubStatuses: Array<{ value: number, label: string, active: boolean }> = [];

    get activeOnHoldSubStatuses() {
        return this.onHoldSubStatuses.filter(ss => ss.active);
    }

    async getOnHoldSubStatuses() {
        try {
            const resp = await this.$http.get("/api/Configuration");
            this.onHoldSubStatuses = resp.data.onHoldSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active
            }));
        } catch {
            //ignore
        }
    }

    subStatusLabel(id: number) {
        return this.onHoldSubStatuses.find(ss => ss.value == id)?.label;
    }
}
</script>
